import React from 'react';
const { useState, useEffect } = React;
import { F } from '../../utils.jsx';
import {
  PRICING_CONFIG, COUNTRY_LIST,
  calculatePrice, formatPrice, detectCountry, detectTeamSize, teamSizeBucket,
  quoteLangForCountry,
} from '../../pricing.js';
import { buildCommercialTerms } from '../../quoteDefaults.js';
import { buildQuoteLines } from '../../quoteLines.js';
import { printProposalPDF } from '../../utils/proposalHtml.js';
import { getAuthHeaders } from '../../globals.js';
import { contentLocked, isAccepted, wasAccepted, QUOTE_RULE_MESSAGES } from '../../quoteRules.js';

// The quote editor (a Diagnostic Intake record): lines, currency, conditions,
// internal analysis, pricing engine and status. Extracted from the company's
// Intake tab (AccountIntakeTab) without changing how it works there, so the same
// editor can open a quote from other places. The record and its actions come
// from outside (onSave, optional onApprove); sending and accepting live in the file (QuoteFile). The server decides what
// may change (src/quoteRules.js): an accepted quote (or an earlier version) keeps its
// content, and this editor only reflects it.
// - `account` (optional): the company, for its name and country.
// - `intakeFields` / `intakeStatus` (optional): the client's form answers, used
//   only by "Armar cotización automática".
// - `manualOnly` (optional): an individual quote. Amounts are written by hand (the
//   pricing engine prices teams), so its card is not shown.

export const INTAKE_FIELDS = [
  { key: 'Company Context',       label: 'Company Context', show: 'Contexto de la empresa', icon: '🏢' },
  { key: 'Country',               label: 'Country', show: 'País', icon: '🌍' },
  { key: 'Current Sales Motion',  label: 'Current Sales Motion', show: 'Cómo vende hoy', icon: '🔄' },
  { key: 'Sales Team Size',       label: 'Sales Team Size', show: 'Tamaño del equipo comercial', icon: '👥' },
  { key: 'Main Challenge',        label: 'Main Challenge', show: 'Desafío principal', icon: '🎯' },
  { key: "What They've Tried",    label: "What They've Tried", show: 'Qué ya probaron', icon: '🔬' },
  { key: 'Goals',                 label: 'Goals with Oike', show: 'Objetivos con Oike', icon: '🚀' },
  { key: 'Pipeline Last 90 Days', label: 'Pipeline (last 90d)', show: 'Pipeline (últimos 90 días)', icon: '📊' },
  { key: 'Prospecting Activity',  label: 'Prospecting Activity', show: 'Prospección', icon: '📤' },
  { key: 'Messaging Examples',    label: 'Messaging Examples', show: 'Ejemplos de mensajes', icon: '✉️' },
  { key: 'Additional Context',    label: 'Additional Context', show: 'Contexto adicional', icon: '📝' },
];

const ALL_CURRENCIES = [...new Set(Object.values(PRICING_CONFIG.countries).map(c => c.currency)), 'ARS'];
const CURRENCIES = ALL_CURRENCIES;
const inputSt = {
  width: '100%', padding: '8px 12px', borderRadius: 7, border: '1px solid var(--globant-border)',
  background: 'rgba(30,42,44,0.04)', color: 'var(--globant-text)', fontSize: 13,
};
const areaSt = { ...inputSt, minHeight: 80, resize: 'vertical', fontFamily: 'inherit' };

function emptyLine() {
  return { service: '', scope: '', onceFee: '', monthlyFee: '', note: '' };
}


const STATUS_LABEL = { Approved: 'Revisada internamente', Draft: 'En preparación', Won: 'Aceptada', Lost: 'Perdida' };

export default function QuoteEditor({
  quote, account = null, intakeFields = {}, intakeStatus = '', isFollowUp = false, contextName = '', manualOnly = false,
  saving = false, onSave, onApprove = null,
}) {
  const fields = intakeFields || {};
  const status = intakeStatus;
  const quoteFields = quote?.fields || {};
  const INTAKE_FIELDS_FOR_AI = INTAKE_FIELDS;
  const [analysis, setAnalysis] = useState('');
  const [lines, setLines] = useState([emptyLine()]);
  const [currency, setCurrency] = useState('USD');
  const [conditions, setConditions] = useState('');
  const [quoteStatus, setQuoteStatus] = useState('');
  const [quoteEdited, setQuoteEdited] = useState(false);

  // Pricing engine state
  const [pricingCountry, setPricingCountry] = useState('');
  const [pricingTeamSize, setPricingTeamSize] = useState('');
  const [pricingServices, setPricingServices] = useState({ diagnostic: false, systemDesign: false, enablement: false, advisory: false });
  const [generatingQuote, setGeneratingQuote] = useState(false);

  // Track which per-line scopes and commercial terms the user has manually edited
  // Keyed by serviceKey (not index) so edits survive service reordering
  const [scopeEdited, setScopeEdited] = useState({});   // serviceKey → true
  const [termsEdited, setTermsEdited] = useState(false);

  useEffect(() => {
    if (!quote) return;
    const f = quote.fields || {};
    setAnalysis(f['Internal Analysis'] || '');
    try { setLines(JSON.parse(f['Quote Lines JSON'] || 'null') || [emptyLine()]); } catch { setLines([emptyLine()]); }
    setCurrency(f['Quote Currency'] || 'USD');
    setConditions(f['Quote Conditions'] || '');
    setQuoteStatus(f['Quote Status'] || '');
    if (f['Quote Market'] && !pricingCountry) setPricingCountry(f['Quote Market']);
    setQuoteEdited(false);
    setScopeEdited({});
    setTermsEdited(false);
  }, [quote?.id]);

  // The status follows the record when an action changes it (approve, back to draft, publish), not only when another quote opens
  useEffect(() => { if (quote) setQuoteStatus(quote.fields?.['Quote Status'] || ''); }, [quote?.fields?.['Quote Status']]);

  // Language for default texts: driven by PRICING_CONFIG per-country lang field
  function quoteLang(countryKey) {
    return quoteLangForCountry(countryKey);
  }

  // Build quote lines from pricing engine + default descriptions.
  // Respects scopeEdited flags: won't overwrite lines the user has manually edited.
  function buildLines(countryKey, teamSize, services, existingLines = []) {
    // Build lookup by service label so we can find the existing line for this service
    const existingByKey = {};
    existingLines.forEach(l => {
      const match = Object.entries(PRICING_CONFIG.services).find(([, s]) => s.label === l.service);
      if (match) existingByKey[match[0]] = l;
    });
    return buildQuoteLines(countryKey, teamSize, services,
      (serviceKey) => (scopeEdited[serviceKey] && existingByKey[serviceKey]?.scope) || null);
  }

  // Auto-populate pricing from intake responses using AI extraction + pricing engine
  const generateQuote = async () => {
    setGeneratingQuote(true);
    try {
      const intakeText = INTAKE_FIELDS_FOR_AI.map(f => {
        const v = fields[f.key];
        return v ? `${f.label}:\n${v}` : null;
      }).filter(Boolean).join('\n\n');

      // Use AI to extract structured pricing inputs
      const validCountries = Object.keys(PRICING_CONFIG.countries);
      const validServices  = Object.keys(PRICING_CONFIG.services);
      let extracted = null;
      try {
        const prompt = `You are analyzing a B2B consulting intake form. Extract the following fields as JSON.

INTAKE RESPONSES:
${intakeText}

Extract:
- country: one of [${validCountries.join(', ')}] — infer from context clues (language, companies mentioned, currency, location). If unclear, return null.
- commercialTeamSize: integer (number of people directly involved in revenue: founders who sell, sales, BDR, SDR, AE, sales leadership). DO NOT count HR, finance, product, engineering unless they sell. If unclear, return null.
- recommendedServices: array of service keys. Choose from: ${validServices.join(', ')}. Include diagnostic always if it's a first engagement. Add systemDesign if they lack a sales process. Add enablement if team training is needed. Add advisory if ongoing support is mentioned.
- analysisNotes: 2-3 sentence internal summary of the commercial situation, main challenge, and what Oike should focus on.

Return ONLY valid JSON. Example:
{"country":"argentina","commercialTeamSize":4,"recommendedServices":["diagnostic","systemDesign"],"analysisNotes":"..."}`;

        const res = await fetch('/api/openai', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            messages: [{ role: 'user', content: prompt }],
            temperature: 0.2,
            max_tokens: 400,
            response_format: { type: 'json_object' },
          }),
        });
        if (res.ok) {
          const data = await res.json();
          const raw = (data.choices?.[0]?.message?.content || data.content || '').trim();
          // Strip markdown fences if model wrapped the JSON
          const clean = raw.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '').trim();
          const parsed = JSON.parse(clean);
          // Normalize, then validate — the model may answer "Spain" or "4" instead of spain / 4
          const rawCountry = typeof parsed.country === 'string' ? parsed.country.trim() : '';
          const country = validCountries.includes(rawCountry.toLowerCase())
            ? rawCountry.toLowerCase()
            : (rawCountry ? detectCountry(rawCountry) : null);
          const size = parseInt(parsed.commercialTeamSize, 10);
          const services = (parsed.recommendedServices || []).filter(s => validServices.includes(s));
          extracted = {
            country,
            commercialTeamSize: size > 0 ? size : null,
            recommendedServices: services.length > 0 ? services : ['diagnostic'],
            analysisNotes: typeof parsed.analysisNotes === 'string' ? parsed.analysisNotes : '',
          };
          console.info('[generateQuote] AI extraction:', parsed);
        } else {
          const errBody = await res.json().catch(() => ({}));
          console.warn('[generateQuote] AI request failed:', res.status, errBody.error || '');
        }
      } catch (aiErr) {
        console.warn('[generateQuote] AI extraction failed, falling back to heuristics:', aiErr?.message || aiErr);
      }

      // Heuristic fallback: whole extraction if AI failed, per field if AI left one empty
      const allText = Object.values(fields).filter(v => typeof v === 'string').join(' ');
      if (!extracted) {
        console.warn('[generateQuote] Using heuristic fallback');
        extracted = {
          country: null,
          commercialTeamSize: null,
          recommendedServices: ['diagnostic', 'systemDesign'],
          analysisNotes: '',
        };
      }
      // Country: what the client chose in the intake, then the account's Country, then AI, then heuristics
      const countryFromLabel = (txt) => {
        if (!txt) return null;
        const byLabel = COUNTRY_LIST.find(c => c.label.toLowerCase() === String(txt).trim().toLowerCase());
        return byLabel ? byLabel.key : detectCountry(String(txt));
      };
      const explicitCountry = countryFromLabel(fields['Country']) || countryFromLabel(F(account, 'Country'));
      if (explicitCountry) extracted.country = explicitCountry;
      if (!extracted.country) extracted.country = detectCountry(allText);
      if (!extracted.commercialTeamSize) extracted.commercialTeamSize = detectTeamSize(fields['Sales Team Size'] || '');

      const countryKey = extracted.country || '';
      const teamSize = extracted.commercialTeamSize || null;
      const services = extracted.recommendedServices || ['diagnostic'];

      // Update UI selectors
      if (countryKey) setPricingCountry(countryKey);
      if (teamSize) setPricingTeamSize(String(teamSize));
      const serviceMap = { diagnostic: false, systemDesign: false, enablement: false, advisory: false };
      services.forEach(s => { if (s in serviceMap) serviceMap[s] = true; });
      setPricingServices(serviceMap);

      if (extracted.analysisNotes) {
        setAnalysis(extracted.analysisNotes);
      }

      // Build quote lines + commercial terms from pricing engine
      if (countryKey && teamSize) {
        const country = PRICING_CONFIG.countries[countryKey];
        if (country) setCurrency(country.currency);

        const newLines = buildLines(countryKey, teamSize, services, lines);
        if (newLines.length > 0) {
          setLines(newLines);
          setScopeEdited({});  // reset per-line edit flags on a fresh AI generation
          if (!termsEdited) {
            const lang = quoteLang(countryKey);
            setConditions(buildCommercialTerms(services, lang, { teamSize, countryName: country?.label || '' }));
          }
          setQuoteEdited(true);
          window.__oikeToast?.('Quote generated — review and adjust before saving', 'success');
        } else {
          window.__oikeToast?.('Country or team size not detected — fill selectors and try again', 'error');
        }
      } else {
        const missing = [!countryKey && 'country', !teamSize && 'team size'].filter(Boolean).join(' and ');
        window.__oikeToast?.(`Could not detect ${missing} — fill selectors manually`, 'error');
      }
    } catch (e) {
      console.error('[generateQuote]', e);
      window.__oikeToast?.('Failed to generate quote: ' + e.message, 'error');
    }
    setGeneratingQuote(false);
  };

  // Recalculate lines from current selectors (manual recalculation)
  const recalculateLines = () => {
    const countryKey = pricingCountry;
    const teamSize = parseInt(pricingTeamSize, 10);
    if (!countryKey || isNaN(teamSize) || teamSize <= 0) {
      window.__oikeToast?.('Set country and team size first', 'error'); return;
    }
    const country = PRICING_CONFIG.countries[countryKey];
    if (country) setCurrency(country.currency);
    const selected = Object.entries(pricingServices).filter(([, v]) => v).map(([k]) => k);
    if (selected.length === 0) { window.__oikeToast?.('Select at least one service', 'error'); return; }

    const newLines = buildLines(countryKey, teamSize, selected, lines);
    setLines(newLines);
    if (!termsEdited) {
      const lang = quoteLang(countryKey);
      setConditions(buildCommercialTerms(selected, lang, { teamSize, countryName: country?.label || '' }));
    }
    setQuoteEdited(true);
    window.__oikeToast?.('Quote lines recalculated', 'success');
  };

  const handleSaveQuote = () => {
    const cleanLines = lines.filter(l => l.service.trim()).map(l => ({
      service: l.service.trim(),
      scope: l.scope?.trim() || '',
      onceFee: l.onceFee !== '' && l.onceFee !== null ? Number(l.onceFee) : null,
      monthlyFee: l.monthlyFee !== '' && l.monthlyFee !== null ? Number(l.monthlyFee) : null,
      note: l.note?.trim() || '',
    }));
    if (cleanLines.length === 0) { window.__oikeToast('Agregá al menos una línea de servicio', 'error'); return; }
    const quoteFields = {
      'Internal Analysis': analysis,
      'Quote Lines JSON': JSON.stringify(cleanLines),
      'Quote Currency': currency,
      'Quote Conditions': conditions,
      'Quote Status': 'Draft',
      ...(pricingCountry ? { 'Quote Market': pricingCountry } : {}),
    };
    onSave(quoteFields);
    setQuoteEdited(false);
  };

  // Accepted or published: the content stays as it is (the server refuses any change; src/quoteRules.js)
  const hasLinkedLanding = contentLocked(quoteFields);
  const accepted = isAccepted(quoteFields);
  const savingQuote = saving;
  const approveQuote = onApprove;

  return (
    <>
          <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--globant-muted)', letterSpacing: '1px', textTransform: 'uppercase', marginTop: 8 }}>
            Internal Analysis & Quote
          </div>
          {isFollowUp && (
            <div className="card" style={{ borderLeft: '3px solid #6B5CA5', fontSize: 12, color: 'var(--globant-muted)' }}>
              Cotización de seguimiento creada desde el Oike Diagnostic ({quote.fields?.['Name'] || 'sin nombre'}). La cotización original del intake sigue guardada sin cambios.
            </div>
          )}

          {/* ── Pricing engine (teams); an individual quote is written by hand ── */}
          {manualOnly ? (
            <div style={{ fontSize: 12, color: 'var(--globant-muted)' }}>Cotización individual: los importes se escriben a mano en las líneas.</div>
          ) : (
          <div className="card" style={{ borderLeft: '3px solid var(--globant-green)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--globant-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>Motor de precios</div>
              <button className="action-btn btn-primary" style={{ fontSize: 11 }}
                onClick={generateQuote} disabled={generatingQuote || hasLinkedLanding || status !== 'Submitted'}>
                {generatingQuote ? 'Analizando…' : 'Armar cotización automática'}
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--globant-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Mercado</div>
                <select value={pricingCountry} onChange={e => setPricingCountry(e.target.value)} disabled={hasLinkedLanding}
                  style={{ ...inputSt, fontSize: 12 }}>
                  <option value="">Seleccioná un país</option>
                  {COUNTRY_LIST.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--globant-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Tamaño del equipo comercial</div>
                <input type="number" min="1" max="500" style={{ ...inputSt, fontSize: 12 }}
                  value={pricingTeamSize} onChange={e => setPricingTeamSize(e.target.value)} disabled={hasLinkedLanding}
                  placeholder="Ej.: 4" />
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--globant-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Servicios a cotizar</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {Object.entries(PRICING_CONFIG.services).map(([key, svc]) => {
                  const on = pricingServices[key];
                  const result = pricingCountry && pricingTeamSize
                    ? calculatePrice(pricingCountry, key, parseInt(pricingTeamSize, 10))
                    : null;
                  return (
                    <button key={key} disabled={hasLinkedLanding}
                      onClick={() => setPricingServices(p => ({ ...p, [key]: !p[key] }))}
                      style={{ padding: '6px 14px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: hasLinkedLanding ? 'not-allowed' : 'pointer',
                        background: on ? 'rgba(91,191,181,0.15)' : 'rgba(30,42,44,0.04)',
                        border: `1px solid ${on ? 'rgba(91,191,181,0.4)' : 'var(--globant-border)'}`,
                        color: on ? 'var(--globant-green)' : 'var(--globant-muted)',
                        display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2,
                      }}>
                      <span>{svc.label}</span>
                      {result && (
                        <span style={{ fontSize: 9, fontWeight: 700, opacity: 0.8 }}>
                          {formatPrice(result)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {!hasLinkedLanding && (
              <button className="action-btn btn-ghost" style={{ fontSize: 11 }} onClick={recalculateLines}
                disabled={!pricingCountry || !pricingTeamSize}>
                🔄 Recalculate lines
              </button>
            )}

            {pricingCountry && pricingTeamSize && (() => {
              const bucket = teamSizeBucket(parseInt(pricingTeamSize, 10));
              const country = PRICING_CONFIG.countries[pricingCountry];
              return (
                <div style={{ marginTop: 10, fontSize: 11, color: 'var(--globant-muted)' }}>
                  {country?.label} · {bucket === '30+' ? '30+ personas → precio a medida' : `${bucket} personas`}
                </div>
              );
            })()}
          </div>
          )}

          <div className="card">
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--globant-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Análisis interno</div>
            <textarea style={areaSt} value={analysis}
              onChange={e => { setAnalysis(e.target.value); setQuoteEdited(true); }}
              placeholder="Notas internas: diagnóstico, riesgos, oportunidades…"
              disabled={hasLinkedLanding}
            />
          </div>

          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--globant-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>Líneas de la cotización</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, color: 'var(--globant-muted)' }}>Moneda:</span>
                <select value={currency} onChange={e => { setCurrency(e.target.value); setQuoteEdited(true); }} disabled={hasLinkedLanding}
                  style={{ ...inputSt, width: 80, padding: '4px 8px', fontSize: 12 }}>
                  {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--globant-border)' }}>
                    {['Servicio *', 'Alcance', `Único (${currency})`, `Mensual (${currency})`, 'Nota', ''].map(h => (
                      <th key={h} style={{ padding: '6px 8px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: 'var(--globant-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, i) => (
                    <tr key={i}>
                      <td style={{ padding: '4px 4px' }}>
                        <input style={{ ...inputSt, width: '100%' }} value={line.service} disabled={hasLinkedLanding}
                          onChange={e => { const n=[...lines]; n[i]={...n[i],service:e.target.value}; setLines(n); setQuoteEdited(true); }}
                          placeholder="Nombre del servicio" />
                      </td>
                      <td style={{ padding: '4px 4px' }}>
                        <input style={{ ...inputSt, width: '100%' }} value={line.scope} disabled={hasLinkedLanding}
                          onChange={e => { const n=[...lines]; n[i]={...n[i],scope:e.target.value}; setLines(n); const svcMatch=Object.entries(PRICING_CONFIG.services).find(([,s])=>s.label===n[i].service); if(svcMatch) setScopeEdited(p=>({...p,[svcMatch[0]]:true})); setQuoteEdited(true); }}
                          placeholder="Descripción breve" />
                      </td>
                      <td style={{ padding: '4px 4px' }}>
                        <input style={{ ...inputSt, width: 110 }} type="number" min="0" value={line.onceFee ?? ''} disabled={hasLinkedLanding}
                          onChange={e => { const n=[...lines]; n[i]={...n[i],onceFee:e.target.value}; setLines(n); setQuoteEdited(true); }}
                          placeholder="—" />
                      </td>
                      <td style={{ padding: '4px 4px' }}>
                        <input style={{ ...inputSt, width: 110 }} type="number" min="0" value={line.monthlyFee ?? ''} disabled={hasLinkedLanding}
                          onChange={e => { const n=[...lines]; n[i]={...n[i],monthlyFee:e.target.value}; setLines(n); setQuoteEdited(true); }}
                          placeholder="—" />
                      </td>
                      <td style={{ padding: '4px 4px' }}>
                        <input style={{ ...inputSt, width: '100%' }} value={line.note ?? ''} disabled={hasLinkedLanding}
                          onChange={e => { const n=[...lines]; n[i]={...n[i],note:e.target.value}; setLines(n); setQuoteEdited(true); }}
                          placeholder="Nota" />
                      </td>
                      <td style={{ padding: '4px 4px' }}>
                        {!hasLinkedLanding && lines.length > 1 && (
                          <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--globant-muted)', fontSize: 16 }}
                            onClick={() => { setLines(lines.filter((_,j)=>j!==i)); setQuoteEdited(true); }}>✕</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!hasLinkedLanding && (
              <button className="action-btn btn-ghost" style={{ fontSize: 11, marginTop: 8 }}
                onClick={() => { setLines([...lines, emptyLine()]); setQuoteEdited(true); }}>
                + Agregar línea
              </button>
            )}
          </div>

          <div className="card">
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--globant-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Condiciones comerciales</div>
            <textarea style={areaSt} value={conditions} disabled={hasLinkedLanding}
              onChange={e => { setConditions(e.target.value); setTermsEdited(true); setQuoteEdited(true); }}
              placeholder="Vigencia, forma de pago, exclusiones, niveles de servicio…"
            />
          </div>

          <div className="card" style={{ borderLeft: `3px solid ${quoteStatus === 'Approved' ? '#1E7B4F' : '#6B5CA5'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--globant-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Contenido</div>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '3px 12px', borderRadius: 20,
                  background: quoteStatus === 'Approved' ? 'rgba(52,211,153,0.15)' : quoteStatus === 'Draft' ? 'rgba(167,139,250,0.12)' : 'rgba(156,163,175,0.12)',
                  color: quoteStatus === 'Approved' ? '#1E7B4F' : quoteStatus === 'Draft' ? '#6B5CA5' : 'var(--globant-muted)',
                  border: `1px solid ${quoteStatus === 'Approved' ? 'rgba(52,211,153,0.3)' : quoteStatus === 'Draft' ? 'rgba(167,139,250,0.25)' : 'var(--globant-border)'}`,
                }}>
                  {STATUS_LABEL[quoteStatus] || '— Sin cotización'}
                </span>
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {lines.some(l => l.service?.trim()) && (
                  <button className="action-btn btn-ghost" style={{ fontSize: 12 }}
                    onClick={() => printProposalPDF({
                      accountName: F(account, 'Account Name') || contextName || quoteFields['Name'] || 'Client',
                      currency,
                      lines: lines.filter(l => l.service?.trim()),
                      conditions,
                      lang: quoteLang(pricingCountry),
                      isDraft: quoteEdited,
                    })}>
                    Descargar PDF
                  </button>
                )}
                {!hasLinkedLanding && (
                  <button className="action-btn btn-primary" style={{ fontSize: 12 }}
                    onClick={handleSaveQuote} disabled={savingQuote || !quoteEdited}>
                    {savingQuote ? 'Guardando…' : 'Guardar cambios'}
                  </button>
                )}
                {/* An internal approval is optional: it is not needed to send or to register the acceptance */}
                {!hasLinkedLanding && onApprove && quoteStatus === 'Draft' && (
                  <button className="action-btn btn-ghost" style={{ fontSize: 12 }}
                    onClick={approveQuote} disabled={savingQuote || quoteEdited}>
                    {savingQuote ? 'Guardando…' : 'Marcar como revisada (opcional)'}
                  </button>
                )}
                {hasLinkedLanding && (
                  <div style={{ fontSize: 12, color: '#1E7B4F', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, maxWidth: 520 }}>
                    {accepted ? QUOTE_RULE_MESSAGES.quote_accepted : wasAccepted(quoteFields) ? QUOTE_RULE_MESSAGES.quote_was_accepted : QUOTE_RULE_MESSAGES.quote_replaced}
                  </div>
                )}
              </div>
            </div>

            {!hasLinkedLanding && quoteEdited && (
              <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.25)', borderRadius: 6, fontSize: 11, color: '#9A6212' }}>
                Hay cambios sin guardar.
              </div>
            )}
          </div>
    </>
  );
}
